import { useEffect } from 'react'
import { initMetaPixel, META_PIXEL_ID } from '../utils/metaPixel'

const INTERACTIONS: Array<keyof WindowEventMap> = [
  'pointerdown',
  'keydown',
  'scroll',
]

/**
 * Installs the Meta Pixel after the page is interactive — on browser idle time
 * or on the visitor's first interaction — so tracking never delays first paint.
 */
export function useMetaPixel(pixelId: string = META_PIXEL_ID): void {
  useEffect(() => {
    let done = false

    const load = () => {
      if (done) return
      done = true
      cleanup()
      initMetaPixel(pixelId)
    }

    const idleWindow = window as Window & {
      requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
      cancelIdleCallback?: (handle: number) => void
    }

    let idleHandle: number | undefined
    let timeoutHandle: number | undefined

    function cleanup() {
      INTERACTIONS.forEach((event) => window.removeEventListener(event, load))
      if (idleHandle !== undefined && idleWindow.cancelIdleCallback) {
        idleWindow.cancelIdleCallback(idleHandle)
      }
      if (timeoutHandle !== undefined) window.clearTimeout(timeoutHandle)
    }

    INTERACTIONS.forEach((event) =>
      window.addEventListener(event, load, { once: true, passive: true }),
    )

    if (idleWindow.requestIdleCallback) {
      idleHandle = idleWindow.requestIdleCallback(load, { timeout: 4000 })
    } else {
      timeoutHandle = window.setTimeout(load, 2500)
    }

    return cleanup
  }, [pixelId])
}
